import React, {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {FieldArray, useFormikContext} from 'formik';
import PriceButton from './PriceButton/PriceButton';
import {Button, Helper} from '@tickchak/react-share/app';
import {add, dollar, star} from '../../../../../../assets/symbols/symbols/Symbols.css';
import TooltipAbsolute from '../../../../../../component/tooltip/tooltipAbsolute/TooltipAbsolute';
import PricingPop from '../../../pricingPop/PricingPop';
import useShow from '../../../../../../hooks/useShow';
import * as css from '../PricingStep/PricingStep.css';
import {SubscriptionFormValues} from '../../createSubscriptionForm.types';
import {Price} from '../../../../../../redux/subscription/subscription.type';
import {formatNumber} from '../../../../../../library/numberFunction';
import {PricingFormValues} from '../../../pricingPop/PricingPop';

type ArrayHelpers = Parameters<React.ComponentProps<typeof FieldArray>['render']>[0];

const getPriceId = (price: Price): string => {
    return price.tpid?.toString() || price.tempId || '';
};

function SubscriptionPriceSection() {
    const {t} = useTranslation('subscriptions');
    const {values, setFieldValue, errors, touched} = useFormikContext<SubscriptionFormValues>();
    const prices = values.prices || [];
    const selectedPrices = values.selectedPrices || [];
    const arrayHelpersRef = useRef<ArrayHelpers | null>(null);
    const pricingPopShow = useShow();
    const [editingPriceId, setEditingPriceId] = useState<string | null>(null);

    const getPriceLabel = (price: Price) => {
        const priceValue = price.price || 0;
        return `${price.title} ${formatNumber(priceValue)}₪`;
    };

    const handleSavePricing = (pricingValues: PricingFormValues) => {
        if(editingPriceId !== null) {
            const priceIndex = prices.findIndex(p => getPriceId(p) === editingPriceId);
            if(priceIndex !== -1) {
                setFieldValue(`prices[${priceIndex}]`, {
                    ...prices[priceIndex],
                    ...pricingValues
                });
            }
        }
        else {
            if(arrayHelpersRef.current) {
                const newPrice: Price = {
                    tpid: null,
                    tempId: crypto.randomUUID(),
                    ...pricingValues
                };
                arrayHelpersRef.current.push(newPrice);
            }
        }
        setEditingPriceId(null);
        pricingPopShow.close();
    };

    const handleEditPrice = (priceId: string) => {
        setEditingPriceId(priceId);
        pricingPopShow.open();
    };

    const handleAddPrice = () => {
        setEditingPriceId(null);
        pricingPopShow.open();
    };

    return (<css.sectionWrapper>
        <css.headerTitle $icon={dollar}>
            {t('subscriptionPrice')}
            <Helper
                title={t('subscriptionPriceHelperTitle')}
                content={<css.helperContent>{t('subscriptionPriceHelperContent')}</css.helperContent>}
                size='small'
            />
            {errors.prices && touched.prices && (
                <TooltipAbsolute show={true} text={typeof errors.prices === 'string' ? errors.prices : ''} dataTitleIcon={undefined} icon={star} />
            )}
        </css.headerTitle>
        <css.section>
            <FieldArray
                name="prices"
                render={arrayHelpers => {
                    arrayHelpersRef.current = arrayHelpers;
                    return (
                        <css.priceButtons>
                            {prices.map((price: Price, index: number) => {
                                const priceId = getPriceId(price);
                                const priceIdString = price.tpid?.toString();
                                return (
                                    <PriceButton
                                        key={priceId}
                                        label={getPriceLabel(price)}
                                        isSelected={selectedPrices.includes(priceIdString)}
                                        onSelect={() => handleEditPrice(priceId)}
                                        onEdit={() => handleEditPrice(priceId)}
                                        onDelete={() => {
                                            arrayHelpers.remove(index);
                                            if(priceIdString && selectedPrices.includes(priceIdString)) {
                                                setFieldValue('selectedPrices', selectedPrices.filter((p: string) => p !== priceIdString));
                                            }
                                        }}
                                    />
                                );
                            })}
                            <Button iconStart={<css.priceIcon icon={add} />} variant="brand" size="category" onClick={handleAddPrice} >{' '}</Button>
                        </css.priceButtons>
                    );
                }}
            />
            {pricingPopShow.isOpen && (
                <PricingPop
                    show={pricingPopShow}
                    onSave={handleSavePricing}
                    priceId={editingPriceId}
                />
            )}
        </css.section>
    </css.sectionWrapper>
    );
}

export default SubscriptionPriceSection;

